import { useEffect, useState } from "react";
import {
  fetchConferenceByHost,
  fetchCountries,
  fetchFocusedSessionAvailability as apiFetchFocusedSessionAvailability,
  fetchFocusedSessions as apiFetchFocusedSessions,
  fetchGenderOptions as apiFetchGenderOptions,
  fetchMasterDetails,
} from "../api/conferenceRegistrationApi";

const DEFAULT_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Australia",
  "Canada",
].map((name) => ({
  label: name,
  value: name,
}));

const normalizeConferenceResponse = (data) =>
  data?.conference || data?.data || data || {};

const resolveConferenceId = (data) =>
  data?.id ?? data?.conferenceId ?? data?.dbId ?? null;

const fetchFirstMasterDetails = async ({ conferenceApi, conferenceId, types }) => {
  for (const type of types) {
    try {
      const details = await fetchMasterDetails({
        conferenceApi,
        conferenceId,
        type,
      });

      if (details.length > 0) {
        return details;
      }
    } catch (error) {
      console.warn(`Unable to load master details for type "${type}"`, error);
    }
  }

  return [];
};

export const useRegistrationLookups = ({
  conferenceApi,
  currentHost,
  setFormData,
}) => {
  const [bannerUrl, setBannerUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [registrationTypes, setRegistrationTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [conferenceId, setConferenceId] = useState(null);
  const [conferenceName, setConferenceName] = useState(null);
  const [focusedSessions, setFocusedSessions] = useState([]);
  const [focusedSessionAvailability, setFocusedSessionAvailability] = useState({});
  const [genderOptions, setGenderOptions] = useState([]);
  const [registrationOptionsLoading, setRegistrationOptionsLoading] =
    useState(true);
  const [registrationOptionsError, setRegistrationOptionsError] = useState("");

  const fetchFocusedSessionAvailability = async (sessionId) => {
    if (!sessionId) {
      return null;
    }

    try {
      return await apiFetchFocusedSessionAvailability({
        conferenceApi,
        sessionId,
      });
    } catch (error) {
      console.error(
        `Error fetching focused session availability for ${sessionId}:`,
        error
      );
      return null;
    }
  };

  const loadFocusedSessionAvailability = async (sessionsArray) => {
    const availabilityResults = await Promise.all(
      sessionsArray.map(async (session) => {
        try {
          const availability = await fetchFocusedSessionAvailability(session.id);
          return availability ? [session.id, availability] : null;
        } catch (availabilityError) {
          console.error(
            `Error fetching availability for focused session ${session.id}:`,
            availabilityError
          );
          return null;
        }
      })
    );

    return availabilityResults.reduce((acc, entry) => {
      if (entry) {
        const [sessionId, availability] = entry;
        acc[sessionId] = availability;
      }
      return acc;
    }, {});
  };

  const fetchFocusedSessions = async () => {
    if (!conferenceId) {
      setFocusedSessions([]);
      setFocusedSessionAvailability({});
      return;
    }

    try {
      const sessionsArray = await apiFetchFocusedSessions({
        conferenceApi,
        conferenceId,
      });
      setFocusedSessions(sessionsArray);

      const availabilityMap = await loadFocusedSessionAvailability(sessionsArray);
      setFocusedSessionAvailability(availabilityMap);
    } catch (error) {
      console.error("Error fetching focused sessions:", error);
      setFocusedSessions([]);
      setFocusedSessionAvailability({});
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setRegistrationOptionsLoading(true);
      setRegistrationOptionsError("");

      try {
        const rawConferenceData = await fetchConferenceByHost({
          conferenceApi,
          host: currentHost,
        });
        const data = normalizeConferenceResponse(rawConferenceData);

        if (data.bannerUrl) setBannerUrl(data.bannerUrl);
        if (data.prefix) {
          setFormData((currentFormData) => ({
            ...currentFormData,
            prefix: data.prefix,
          }));
        }

        if (data.name) {
          setConferenceName(data.name);
          setFormData((currentFormData) => ({
            ...currentFormData,
            conference: data.name,
          }));
        }

        const confId = resolveConferenceId(data);
        if (!confId) {
          setRegistrationOptionsError(
            "Unable to load registration options because conference details are incomplete."
          );
          setCategories([]);
          setRegistrationTypes([]);
          setDepartments([]);
          setFocusedSessions([]);
          setFocusedSessionAvailability({});
          return;
        }

        setConferenceId(confId);

        const [
          categoryDetails,
          registrationTypeDetails,
          departmentDetails,
          focusedSessionDetails,
        ] = await Promise.all([
          fetchFirstMasterDetails({
            conferenceApi,
            conferenceId: confId,
            types: ["category", "Category"],
          }),
          fetchFirstMasterDetails({
            conferenceApi,
            conferenceId: confId,
            types: [
              "conferenceType",
              "ConferenceType",
              "registrationType",
              "RegistrationType",
            ],
          }),
          fetchFirstMasterDetails({
            conferenceApi,
            conferenceId: confId,
            types: ["Department", "department"],
          }),
          fetchFirstMasterDetails({
            conferenceApi,
            conferenceId: confId,
            types: ["FocussedSessions", "FocusedSessions", "focussedSessions"],
          }),
        ]);

        setCategories(categoryDetails);
        setRegistrationTypes(registrationTypeDetails);
        setDepartments(departmentDetails);
        setFocusedSessions(focusedSessionDetails);

        const availabilityMap =
          await loadFocusedSessionAvailability(focusedSessionDetails);
        setFocusedSessionAvailability(availabilityMap);

        if (categoryDetails.length === 0 && registrationTypeDetails.length === 0) {
          setRegistrationOptionsError(
            "No category or registration type options were returned for this conference."
          );
          console.warn("No registration master details returned", {
            conferenceId: confId,
            conference: rawConferenceData,
          });
        }
      } catch (error) {
        console.error("Failed to fetch conference data:", error);
        setRegistrationOptionsError(
          "Unable to load registration options. Please refresh or contact support."
        );
        setCategories([]);
        setRegistrationTypes([]);
        setDepartments([]);
        setFocusedSessions([]);
        setFocusedSessionAvailability({});
      } finally {
        setRegistrationOptionsLoading(false);
      }
    };

    const loadCountries = async () => {
      try {
        const list = await fetchCountries();
        setCountries(list.length ? list : DEFAULT_COUNTRIES);
      } catch {
        setCountries(DEFAULT_COUNTRIES);
      }
    };

    loadInitialData();
    loadCountries();
  }, [conferenceApi, currentHost, setFormData]);

  useEffect(() => {
    const loadGenderOptions = async () => {
      if (!conferenceId) {
        setGenderOptions([]);
        return;
      }

      try {
        const genderArray = await apiFetchGenderOptions({
          conferenceApi,
          conferenceId,
        });
        setGenderOptions(genderArray);
      } catch (error) {
        console.error("Error fetching gender options:", error);
        setGenderOptions([]);
      }
    };

    loadGenderOptions();
  }, [conferenceApi, conferenceId]);

  return {
    bannerUrl,
    categories,
    registrationTypes,
    departments,
    countries,
    conferenceId,
    conferenceName,
    focusedSessions,
    setFocusedSessions,
    focusedSessionAvailability,
    setFocusedSessionAvailability,
    genderOptions,
    registrationOptionsLoading,
    registrationOptionsError,
    fetchFocusedSessions,
  };
};
